import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { PermissionKeySchema } from '../access/permissions/permission-key/permission-key';
import { UserStatusSchema } from '../users/user-status';

/**
 * Which kind of token this is. Carried **inside** the payload and checked on
 * every verification: the two secrets already differ, but a `typ` check means a
 * future single-secret configuration cannot silently let a refresh token open
 * an authenticated endpoint.
 */
export const TOKEN_TYPES = ['access', 'refresh'] as const;
export const TokenTypeSchema = z.enum(TOKEN_TYPES);
export type TokenType = (typeof TOKEN_TYPES)[number];

/**
 * The access token's payload.
 *
 * It carries the actor's **resolved** authorization state — roles, granted and
 * denied keys, super-admin flag, lifecycle status — so `PermissionsGuard`
 * authorizes at the edge with no database round-trip (03-access-control).
 *
 * The obvious risk of edge authorization is staleness, and `ver` is the answer:
 * every access mutation, password change, force-logout, ban and suspension
 * bumps `users.tokenVersion`, which is checked per request against a
 * Redis-cached value. A revoked token therefore dies on its **next** request
 * rather than at the end of its TTL — the short TTL is defence in depth, not
 * the revocation mechanism (N8/N22).
 */
export const AccessTokenClaimsSchema = z.object({
  sub: UuidSchema,
  typ: z.literal('access'),
  ver: z.number().int().min(0),
  status: UserStatusSchema,
  roles: z.array(z.string()),
  granted: z.array(PermissionKeySchema),
  denied: z.array(PermissionKeySchema),
  /** `super_admin` — bypasses permission evaluation entirely. */
  sa: z.boolean(),
});

export type AccessTokenClaims = z.infer<typeof AccessTokenClaimsSchema>;

/**
 * The refresh token's payload. Carries **no** authorization state on purpose:
 * a refresh re-resolves roles and permissions from the database, which is what
 * makes a permission edit take effect on the next refresh even for a client
 * that never sees a 401.
 *
 * `jti` is the persisted `refresh_tokens` row — the handle rotation and
 * reuse-detection work on.
 */
export const RefreshTokenClaimsSchema = z.object({
  sub: UuidSchema,
  typ: z.literal('refresh'),
  ver: z.number().int().min(0),
  jti: UuidSchema,
});

export type RefreshTokenClaims = z.infer<typeof RefreshTokenClaimsSchema>;
